import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { t } from 'i18next';
import { css } from '@emotion/react';
import HeatmapModule from 'highcharts/modules/heatmap';
import { allHeatmapGraphData } from './useFetchBenGraphData'
import {AverageHeatmapGraph} from "./averageHeatmap";
import ContentHeader from './contentHeader'
import HeatmapCaption from "@/pages/residents/detail/benTrendAnalysis/heatmapCaption";
import {heatmapColorStops} from "@/pages/residents/detail/benTrendAnalysis/common";

HeatmapModule(Highcharts);

interface Props {
  heatmapData: allHeatmapGraphData;
  careRecordPlot: { x: number; y: number; color: string; name: string }[];
  notificationPlot: { x: number; y: number; color: string; name: string }[];
  showCareRecordPlot: boolean;
  showNotificationPlot: boolean;
  isPrinting?: boolean;
}

const GraphContainer = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CaptionContainer = css`
  display: flex;
  margin-left: 72px;
`;

export const HumidityTrendHeatmapGraph = React.memo(
  ({
    heatmapData,
    careRecordPlot,
    notificationPlot,
    showCareRecordPlot,
    showNotificationPlot,
    isPrinting = false,
  }: Props) => {

    // Offsets
    const notificationData = notificationPlot.map((r) => ({ ...r, x: r.x + 0.4, y: r.y - 0.3 }));
    const careRecordData = careRecordPlot.map((r) => ({ ...r, x: r.x + 0.4, y: r.y + 0.3 }));

    const xAxisMax = heatmapData.main.xCategories ? heatmapData.main.xCategories.length - 1 : 0;
    const yAxisMax = heatmapData.main.yCategories ? heatmapData.main.yCategories.length - 1 : 0;

    const colorStops = heatmapColorStops['humidity'];
    const _averageStops = heatmapColorStops['commonTrend'];
    const averageCaptionColorStops = [
        _averageStops[0],
        _averageStops[2],
        _averageStops[4],
        _averageStops[5],
        _averageStops[6],
        _averageStops[8],
        _averageStops[10],
    ];

    const contentTitle = t('humidity', '湿度');
    const colorMin = 20;
    const colorMax = 80;
    const valueUnit: string = "%";

    const commonCaptionParams = {
      maxValue: colorMax,
      minValue: colorMin,
      maxUnit: t('high', '高'),
      minUnit: t('low', '低'),
    }

    const option: Highcharts.Options = {
      title: {
        text: '',
      },
      chart: {
        zoomType: 'x',
        marginLeft: 72,
        marginRight: isPrinting ? 180 : 0,
        marginTop: 0,
        marginBottom: 56,
        shadow: false,
        animation: false,
        backgroundColor: '#FFFFFF',
        height: heatmapData.main.yCategories ? heatmapData.main.yCategories.length * 20 + 56 : 100,
      },
      xAxis: {
        categories: heatmapData.main.xCategories,
        min: 0,
        max: xAxisMax,
        tickInterval: 2,
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      yAxis: {
        reversed: false,
        categories: heatmapData.main.yCategories.map((date) => date.split('/').slice(1).join('/')),
        min: 0,
        max: yAxisMax,
        gridLineWidth: 0,
        title: { text: '' },
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      colorAxis: {
        min: colorMin,
        floor: 0,
        max: colorMax,
        stops: colorStops
      },
      series: [
        {
          type: 'heatmap',
          boostThreshold: 100,
          data: heatmapData.main.data,
          colsize: 1,
          rowsize: 1,
          index: 0,
          turboThreshold: Number.MAX_VALUE,
          dataLabels: {
            enabled: true,
            color: '#000000',
            style: {
              fontSize: '12px',
            },
            formatter: function() {
              return Math.floor(this.point.value ?? 0);
            },
          },
          tooltip: {
            headerFormat: '',
            pointFormat: `<b>${t('humidity', '湿度')} {point.value}${valueUnit}</b><br/>`,
          },
        },
        {
          name: t('careHistory', 'ケア履歴'),
          data: careRecordData,
          type: 'scatter',
          yAxis: 0,
          index: 1,
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: '{point.name}<br />',
          },
          marker: {
            radius: 4,
            symbol: 'square',
          },
          stickyTracking: false,
          showInLegend: false,
          visible: showCareRecordPlot,
        },
        {
          name: t('notification', '通知'),
          data: notificationData,
          type: 'scatter',
          yAxis: 0,
          index: 1,
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: '{point.name}<br />',
          },
          marker: {
            radius: 4,
            symbol: `circle`,
          },
          stickyTracking: false,
          showInLegend: false,
          visible: showNotificationPlot,
        },
      ],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      boost: {
        useGPUTranslations: true,
      },
    };

    React.useEffect(() => {
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    }, [isPrinting]);

    return (
      <div>
        <ContentHeader
            title={contentTitle + ' [%]'}
            iconPath={'/images/residents/benTrendAnalysis/humidity.svg'}
            shiftLeft={isPrinting}
        />
        <div css={GraphContainer}>
          <AverageHeatmapGraph averageHeatmap={heatmapData.summary} colorMin={20} colorMax={80} postFix={valueUnit} isPrinting={isPrinting} />
          <HighchartsReact highcharts={Highcharts} options={option} />
        </div>
        <div css={CaptionContainer}>
          <HeatmapCaption
              heatmapStops={averageCaptionColorStops}
              captionTitle={t('average', '平均')}
              {...commonCaptionParams}
          />
          <HeatmapCaption
              heatmapStops={colorStops}
              captionTitle={contentTitle}
              {...commonCaptionParams}
          />
        </div>
      </div>
    );
  }
);
